import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  HelpCircle, MessageSquare, Plus, Search, Book, Video, FileText,
  ChevronRight, Clock, CheckCircle, AlertCircle, X, Send, Loader2
} from 'lucide-react';
import { supportAPI } from '../../api';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { MAX_UPLOAD_MB } from '../../config/uploadLimits';

const HelpCenter = () => {
  const [activeTab, setActiveTab] = useState('faq');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });

  const faqs = [
    {
      question: 'How do I get started with the Virtual Try-On API?',
      answer: 'Start by getting your API key from the API Keys section. Then follow our API documentation to integrate the /v1/tryon endpoint. Upload human and garment images to get VTO results.'
    },
    {
      question: 'What image formats are supported?',
      answer: `We support JPEG, PNG, and WebP formats. Images should be at least 512x512 pixels for best results. Maximum file size is ${MAX_UPLOAD_MB}MB (auto-compressed to optimal size).`
    },
    {
      question: 'How does the credit system work?',
      answer: 'Each VTO generation costs 10 credits (free if cached). Moments generation costs 5 credits. New accounts get 100 free credits. Purchase more credits from the Wallet section.'
    },
    {
      question: 'What is caching and how does it save credits?',
      answer: 'If you generate a VTO with the same human and garment images again, we return the cached result for free (0 credits). Caching helps save costs for repeated requests.'
    },
    {
      question: 'How do I add team members?',
      answer: 'Go to Team Management and click "Add Member". Enter their email and assign a role (Admin, Editor, or Viewer). They will receive an invitation to join your account.'
    },
    {
      question: 'What are the different subscription plans?',
      answer: 'Basic is free with pay-per-use credits. Gold (₹2,000/mo) adds watermarking and rate limits. Premium (₹5,000/mo) includes analytics and priority support.'
    }
  ];

  const guides = [
    { title: 'Getting Started Guide', icon: Book, link: '#' },
    { title: 'API Integration Tutorial', icon: FileText, link: '#' },
    { title: 'Video Walkthrough', icon: Video, link: '#' },
    { title: 'Best Practices', icon: CheckCircle, link: '#' }
  ];

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await supportAPI.getTickets();
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject || !newTicket.description) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await supportAPI.createTicket(newTicket);
      toast.success('Ticket created successfully');
      setShowNewTicket(false);
      setNewTicket({ subject: '', description: '', priority: 'medium', category: 'general' });
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      const response = await supportAPI.getTicket(ticketId);
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Failed to load ticket');
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    
    setSubmitting(true);
    try {
      await supportAPI.replyToTicket(selectedTicket.ticket_id, { message: replyText });
      toast.success('Reply sent');
      setReplyText('');
      handleViewTicket(selectedTicket.ticket_id);
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-700';
      case 'in_progress': return 'bg-amber-100 text-amber-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-blue-100 text-blue-700';
      case 'low': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="help-center-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Help Center</h1>
          <p className="text-slate-600">Get help and support for Nanokart.ai</p>
        </div>
        <button
          onClick={() => setShowNewTicket(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
          data-testid="create-ticket-btn"
        >
          <Plus className="w-4 h-4" />
          Create Ticket
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('faq')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'faq' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <HelpCircle className="w-4 h-4 inline mr-2" />
            FAQ
          </button>
          <button
            onClick={() => setActiveTab('tickets')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'tickets' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            My Tickets ({tickets.length})
          </button>
        </div>

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="p-6">
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.details
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group bg-slate-50 rounded-lg"
                >
                  <summary className="px-4 py-3 cursor-pointer font-medium text-slate-900 flex items-center justify-between">
                    {faq.question}
                    <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-4 pb-4 text-slate-600 text-sm">
                    {faq.answer}
                  </div>
                </motion.details>
              ))}
            </div>

            {/* Quick Links */}
            <div className="mt-8">
              <h3 className="font-semibold text-slate-900 mb-4">Resources</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {guides.map((guide, index) => (
                  <a
                    key={index}
                    href={guide.link}
                    className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <guide.icon className="w-5 h-5 text-blue-600" />
                    <span className="text-slate-700">{guide.title}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">No support tickets yet</p>
                <p className="text-sm text-slate-500">Create a ticket if you need help</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <motion.div
                    key={ticket.ticket_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 cursor-pointer transition-colors"
                    onClick={() => handleViewTicket(ticket.ticket_id)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-slate-900">{ticket.subject}</h4>
                        <p className="text-sm text-slate-500 mt-1 line-clamp-1">{ticket.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(ticket.status)}`}>
                          {ticket.status?.replace('_', ' ')}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {ticket.created_at ? format(parseISO(ticket.created_at), 'MMM d, yyyy') : '-'}
                      </span>
                      <span className="capitalize">{ticket.category}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-lg w-full p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">Create Support Ticket</h3>
              <button onClick={() => setShowNewTicket(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  placeholder="Brief description of your issue"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="general">General</option>
                    <option value="billing">Billing</option>
                    <option value="technical">Technical</option>
                    <option value="api">API</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  rows={4}
                  placeholder="Describe your issue in detail..."
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewTicket(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Ticket
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
          >
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{selectedTicket.subject}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(selectedTicket.status)}`}>
                      {selectedTicket.status?.replace('_', ' ')}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                      {selectedTicket.priority}
                    </span>
                    <span className="text-xs text-slate-500">
                      {selectedTicket.created_at ? format(parseISO(selectedTicket.created_at), 'MMM d, yyyy HH:mm') : '-'}
                    </span>
                  </div>
                </div>
                <button onClick={() => setSelectedTicket(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {selectedTicket.messages?.map((msg, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg ${
                    msg.sender === 'partner' ? 'bg-blue-50 ml-8' : 'bg-slate-50 mr-8'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium capitalize text-slate-900">
                      {msg.sender === 'partner' ? 'You' : 'Support'}
                    </span>
                    <span className="text-xs text-slate-500">
                      {msg.timestamp ? format(parseISO(msg.timestamp), 'MMM d, HH:mm') : '-'}
                    </span>
                  </div>
                  <p className="text-slate-700 text-sm whitespace-pre-wrap">{msg.message}</p>
                </div>
              ))}
            </div>

            {selectedTicket.status !== 'closed' && (
              <div className="p-4 border-t border-slate-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    placeholder="Type your reply..."
                    onKeyDown={(e) => e.key === 'Enter' && handleReply()}
                  />
                  <button
                    onClick={handleReply}
                    disabled={submitting || !replyText.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default HelpCenter;
